import { maLogger } from '@/base/logger'

/**
 * Creates a proxy-based storage manager for the given Storage object and key mapping.
 * This manager allows you to:
 * - Access and modify storage entries via direct property access.
 * - Automatically handle JSON parsing/stringifying.
 * - Optionally use placeholder data for missing keys.
 * - Apply a prefix to all keys.
 * - Clear all managed keys at once with `manager.clear()`.
 *
 * @example
 * ```js
 * import { createStorageManager } from '@/utils/storageManagerFactory';
 * import { SESSION_STORAGE_KEYS } from '@/utils/storage';
 *
 * const sessionStorageManager = createStorageManager(sessionStorage, SESSION_STORAGE_KEYS, {
 *   prefix: 'ma_',
 *   placeholderData: {
 *     lastSessionExpiredShown: false
 *   }
 * });
 *
 * // If not set, returns placeholder:
 * console.log(sessionStorageManager.lastSessionExpiredShown); // false
 *
 * // Set a value:
 * sessionStorageManager.lastSessionExpiredShown = true;
 *
 * // Delete a value:
 * delete sessionStorageManager.lastSessionExpiredShown;
 *
 * // Clear all managed keys:
 * sessionStorageManager.clear();
 * ```
 *
 * @param {Storage} storage - The Web Storage API object (either `localStorage` or `sessionStorage`).
 * @param {Record<string, string>} keys - An object mapping property names to unprefixed storage keys.
 * @param {{ prefix?: string; placeholderData?: Record<string, any> }} [options] - Optional configuration.
 * @param {string} [options.prefix] - A prefix to prepend to all storage keys.
 * @param {Record<string, any>} [options.placeholderData] - Default values returned if no data is found.
 * @returns {{[key: string]: any} & {clear: () => void}} A proxy object for interacting with the provided storage.
 */
export function createStorageManager(storage, keys, options = {}) {
  const {
    prefix = '',
    placeholderData = {},
  } = options

  // We create a base manager object with a clear method
  const manager = {
    /**
     * Clears all keys defined in the `keys` object from the storage.
     * This will remove all prefixed keys associated with this manager.
     */
    clear() {
      for (const prop in keys) {
        const storageKey = keys[prop]
        const prefixedKey = prefix + storageKey
        storage.removeItem(prefixedKey)
      }
    },
  }

  return new Proxy(manager, {
    get(target, prop) {
      // If the `clear` method or any other directly defined property is requested, return it.
      if (prop in target) {
        return target[prop]
      }

      const storageKey = keys[prop]
      if (!storageKey) {
        maLogger.warn(`No storage key found for property "${String(prop)}". Attempted to read a non-existent key.`)
        return undefined
      }

      const prefixedKey = prefix + storageKey
      const val = storage.getItem(prefixedKey)

      if (val === null) {
        // Return placeholder if available
        if (Object.hasOwnProperty.call(placeholderData, prop)) {
          return placeholderData[prop]
        }
        return null
      }

      try {
        return JSON.parse(val)
      }
      catch (error) {
        maLogger.warn(`Failed to parse JSON for key "${prefixedKey}".`, error)
        return val // Return raw string if JSON parse fails
      }
    },

    set(target, prop, value) {
      const storageKey = keys[prop]
      if (!storageKey) {
        maLogger.warn(`No storage key found for property "${String(prop)}". Failed to set value.`, { value })
        return false
      }

      const prefixedKey = prefix + storageKey
      storage.setItem(prefixedKey, JSON.stringify(value))
      return true
    },

    deleteProperty(target, prop) {
      const storageKey = keys[prop]
      if (!storageKey) {
        maLogger.warn(`No storage key found for property "${String(prop)}". Failed to delete value.`)
        return false
      }

      const prefixedKey = prefix + storageKey
      storage.removeItem(prefixedKey)
      return true
    },
  })
}
